import FormActionButtons from 'components/ui/form-action-buttons';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { BindingProps, EBindingStep } from './types';
import SubmissionCurrencyTabProps from '../submission-currency-tabs';
import { useCallback, useEffect, useState } from 'react';
import { DEFAULT_CONVERSION_CURRENCY } from '../submission-details/types';
import BindingTable from './binding-table';
import ContractDetail from 'components/features/binding/contract-detail';
import {
  ContractResponseDtoContractStatusEnum,
  SubmissionResponseDtoStatusEnum,
} from 'libs/pinnacle-sdk';
import { ConfirmationModal } from 'components/ui/confirmation-modal';
import { ConfirmationModalType } from 'components/ui/confirmation-modal/types';
import { useLocation } from 'react-router';
import { EPageTo } from 'constants/path';

const Binding: React.FC<BindingProps> = ({
  submission,
  includeBack,
  navigateBack,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<string>(
    submission.originalCurrency || DEFAULT_CONVERSION_CURRENCY
  );
  const [bindingStep, setBindingStep] = useState<EBindingStep>(
    EBindingStep.BINDING_DASHBOARD
  );

  const [showUploadSlipConfirmModal, setShowUploadSlipConfirmModal] =
    useState(false);
  const [uploadSlipContractId, setUploadSlipContractId] = useState<
    string | undefined
  >(undefined);

  const location = useLocation();
  const {
    to,
    contractId: contractIdFromLocation,
    contractStatus: contractStatusFromLocationState,
  } = (location.state ?? {}) as {
    to?: EPageTo;
    contractId?: string;
    contractStatus?: ContractResponseDtoContractStatusEnum;
  };

  useEffect(() => {
    // if contract status is pending, stay the binding dashboard otherwise go to set the binding step to contract detail.
    if (
      to === EPageTo.CONTRACT_DETAIL &&
      contractIdFromLocation &&
      contractStatusFromLocationState &&
      contractStatusFromLocationState !==
        ContractResponseDtoContractStatusEnum.Pending
    ) {
      setContractId(contractIdFromLocation);
      setBindingStep(EBindingStep.CONTRACT_DETAIL);
    }
  }, []);

  useEffect(() => {
    if (
      submission.status !== SubmissionResponseDtoStatusEnum.Quoted &&
      submission.status !== SubmissionResponseDtoStatusEnum.Complete
    ) {
      // Skip 1 step to go to the peer review tab
      navigateBack(2);
    }
  }, [navigateBack, submission.status]);

  const [isSlipUpload, setIsSlipUpload] = useState(false);
  const [isCloseContractDetail, setIsCloseContractDetail] = useState(false);

  const [contractId, setContractId] = useState<string | null>(null);

  const handleBindingContract = useCallback(
    (contractId?: string) => {
      setContractId(contractId || null);
      setBindingStep(EBindingStep.CONTRACT_DETAIL);
    },
    [setContractId, setBindingStep]
  );

  const handleSlipUpload = useCallback(
    (contractId?: string) => {
      setIsSlipUpload(true);
      setContractId(contractId || null);
      setBindingStep(EBindingStep.CONTRACT_DETAIL);
      setShowUploadSlipConfirmModal(false);
    },
    [setContractId, setBindingStep]
  );

  const handleCloseContractDetail = useCallback(
    (contractId?: string) => {
      setIsCloseContractDetail(true);
      setContractId(contractId || null);
      setBindingStep(EBindingStep.CONTRACT_DETAIL);
    },
    [setBindingStep]
  );

  const handleBackToBindingDashboard = useCallback(() => {
    setBindingStep(EBindingStep.BINDING_DASHBOARD);
    // reset state
    setIsSlipUpload(false);
    setContractId(null);
    setIsCloseContractDetail(false);
  }, [setBindingStep]);

  return (
    <div className="rounded-lg bg-[#E3F2FB]">
      {bindingStep === EBindingStep.BINDING_DASHBOARD && (
        <>
          <div className="flex items-center justify-between">
            <SubmissionCurrencyTabProps
              originalCurrency={submission.originalCurrency}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          </div>
          <div className="flex flex-col gap-5 mb-4">
            <BindingTable
              submissionId={submission.submissionId}
              handleBindingContract={handleBindingContract}
              submissionStatus={submission.status}
              currency={activeTab}
              originalCurrency={submission.originalCurrency}
              handleSlipUpload={handleSlipUpload}
              handleCloseContractDetail={handleCloseContractDetail}
              handleSlipUploadRowClick={(contractId) => {
                setShowUploadSlipConfirmModal(true);
                setUploadSlipContractId(contractId);
              }}
            />
          </div>

          <FormActionButtons
            cancelText={t('general.back')}
            cancelColor="primary"
            includeCancel={includeBack}
            includeConfirm={false}
            onCancel={() => {
              return navigateBack(2);
            }}
          />
        </>
      )}

      {bindingStep === EBindingStep.CONTRACT_DETAIL && (
        <div className="rounded-lg px-2 bg-white py-4 space-y-4">
          <h1 className="text-2xl font-semibold px-2">Contract Details</h1>
          <ContractDetail
            submission={submission}
            handleBackToBindingDashboard={handleBackToBindingDashboard}
            contractId={contractId}
            isSlipUpload={isSlipUpload}
            hiddenReferenceNumber={!isSlipUpload}
            isCloseContractDetail={isCloseContractDetail}
            isCancelledOrNotTakenUpContract={
              contractStatusFromLocationState ===
                ContractResponseDtoContractStatusEnum.Cancelled ||
              contractStatusFromLocationState ===
                ContractResponseDtoContractStatusEnum.NotTakenUp
            }
          />
        </div>
      )}
      <ConfirmationModal
        isOpen={showUploadSlipConfirmModal}
        onClose={() => setShowUploadSlipConfirmModal(false)}
        onConfirm={() => handleSlipUpload(uploadSlipContractId)}
        title={t('general.actionNeeded')}
        content={t('contractDetail.mustUploadSlip')}
        okText={t('binding.uploadSlip')}
        cancelText={t('general.cancel')}
        type={ConfirmationModalType.PRIMARY}
      />
    </div>
  );
};

export default Binding;
